import { Spin } from 'antd';
import React, { FC, useMemo } from 'react';
import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import {
    formatChartDataForRecharts,
    GoogleAnalyticsData,
} from '../../helpers/analytics-helpers';
import style from './style.module.scss';
import * as Colors from '../../styles/colors';

export interface ChartLine {
    key: string;
    label: string;
    color: string;
}

interface Props {
    chartData: GoogleAnalyticsData[];
    loading: boolean;
    lines: ChartLine[];
}

interface TooltipProps {
    active?: boolean;
    payload?: {
        name: string;
        value: number;
        color: string;
    }[];
    label?: string;
}

const LINE_STYLE = {
    strokeWidth: 2.5,
    dot: { r: 3.5, strokeWidth: 2, fill: Colors.WHITE },
    activeDot: { r: 5 },
};

const CustomTooltip: FC<TooltipProps> = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;

    return (
        <div className={style.custom_tooltip}>
            <p className={style.tooltip_label}>{label}</p>
            {payload.map((entry, index) => (
                <p
                    key={`item-${index}`}
                    className={style.tooltip_item}
                    style={{ color: entry.color }}
                >
                    {entry.name}: {entry.value.toLocaleString()}
                </p>
            ))}
        </div>
    );
};

const AnalyticsChartsInner: FC<Props> = ({ chartData, loading, lines }) => {
    const formattedData = useMemo(() => formatChartDataForRecharts(chartData), [
        chartData,
    ]);

    const tickInterval = useMemo(() => {
        const len = formattedData.length;
        if (len <= 7) return 0;
        if (len <= 30) return Math.floor(len / 7);
        if (len <= 90) return Math.floor(len / 8);
        return Math.floor(len / 10);
    }, [formattedData]);

    return (
        <Spin size="large" tip="One moment..." spinning={loading}>
            <ResponsiveContainer width="100%" height={428}>
                <LineChart
                    data={formattedData}
                    margin={{
                        top: 5,
                        right: 30,
                        left: 20,
                        bottom: 20,
                    }}
                >
                    <CartesianGrid
                        strokeDasharray="4 4"
                        stroke={Colors.UNKNOWN_CT_BLUE_OPACITY_15}
                    />
                    <XAxis
                        dataKey="date"
                        tick={{ fontSize: 11 }}
                        tickMargin={2}
                        angle={-35}
                        textAnchor="end"
                        height={40}
                        interval={tickInterval}
                    />
                    <YAxis
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value: number) =>
                            value.toLocaleString()
                        }
                    />
                    <Tooltip content={<CustomTooltip />} />
                    {lines.length > 1 && <Legend verticalAlign="top" />}
                    {lines.map((line) => (
                        <Line
                            key={line.key}
                            type="monotone"
                            dataKey={line.key}
                            stroke={line.color}
                            name={line.label}
                            {...LINE_STYLE}
                        />
                    ))}
                </LineChart>
            </ResponsiveContainer>
        </Spin>
    );
};

export const AnalyticsCharts = React.memo(AnalyticsChartsInner);
